/**
 * @fileoverview Tipos para datos INE desde Cloudflare R2
 * @description Define la estructura de datos municipales INE cargados desde R2
 * @author PTEL Tool - DataMaster
 * @version 1.0.0
 * @date 2026-02-02
 */

/**
 * Municipio INE con datos completos desde R2
 */
export interface INEMunicipio {
  /** Código INE municipal 5 dígitos: "04001" */
  codMun: string;
  /** Nombre oficial del municipio */
  nombre: string;
  /** Nombre normalizado (mayúsculas, sin acentos) */
  nombreNorm: string;
  /** Nombre de la provincia */
  provincia: string;
  /** Código provincial 2 dígitos: "04" */
  codProv: string;
  /** Coordenada X del centroide en UTM30 ETRS89 */
  centroideX: number;
  /** Coordenada Y del centroide en UTM30 ETRS89 */
  centroideY: number;
  /** Número de infraestructuras críticas en el municipio */
  infraestructuras: number;
}

/**
 * Colección de municipios INE desde R2
 */
export interface INEMunicipioCollection {
  /** Identificador del tipo de datos */
  type: 'INEMunicipioCollection';
  /** Versión de los datos */
  version?: string;
  /** Fecha de generación */
  generatedAt?: string;
  /** Total de registros */
  count?: number;
  /** Array de municipios */
  municipios: INEMunicipio[];
}

/**
 * Resultado de búsqueda INE
 */
export interface INER2LookupResult {
  /** Si se encontró el municipio */
  found: boolean;
  /** Datos del municipio si se encontró */
  municipio?: INEMunicipio;
}

/**
 * Estadísticas del servicio INER2
 */
export interface INER2ServiceStats {
  /** Número de municipios cargados */
  count: number;
  /** Si el servicio está inicializado */
  initialized: boolean;
  /** Timestamp de última carga */
  loadedAt?: number;
  /** Último error si existe */
  error?: string | null;
}

/**
 * Configuración del servicio INER2
 */
export interface INER2ServiceConfig {
  /** URL base del Worker R2 */
  baseUrl?: string;
  /** Timeout para fetch en ms */
  timeout?: number;
  /** Habilitar logging de debug */
  debug?: boolean;
}

/**
 * Centroide de municipio para lookups geográficos
 */
export interface INECentroide {
  /** Coordenada X UTM30 */
  x: number;
  /** Coordenada Y UTM30 */
  y: number;
}

/**
 * Códigos provinciales de Andalucía
 */
export const CODIGOS_PROVINCIALES_ANDALUCIA = [
  '04', // Almería
  '11', // Cádiz
  '14', // Córdoba
  '18', // Granada
  '21', // Huelva
  '23', // Jaén
  '29', // Málaga
  '41', // Sevilla
] as const;

export type CodigoProvincialAndalucia = typeof CODIGOS_PROVINCIALES_ANDALUCIA[number];

/**
 * Verifica si un código provincial pertenece a Andalucía
 */
export function isCodigoProvincialAndalucia(codigo: string): codigo is CodigoProvincialAndalucia {
  return CODIGOS_PROVINCIALES_ANDALUCIA.includes(codigo as CodigoProvincialAndalucia);
}
